import { addDoc, collection } from "firebase/firestore";
import posthog from "posthog-js";
import type { LogEntry } from "../types";
import { auth, db } from "../firebase";
import {
  logEvent as logAnalyticsEvent,
  logScreenMounted as logAnalyticsScreenMounted,
  logPageView as logAnalyticsPageView,
  getAnalyticsDispatchReadiness,
  type AnalyticsDispatchResult,
} from "./analyticsService";
import {
  getAnalyticsQueueDecision,
  shouldMarkFirstVisitAnalyticsDispatched,
} from "./analyticsRecoveryPolicy";
import { getUTMParametersForAnalytics } from "./utmService";
import { onAuthStateChanged } from "firebase/auth";
import {
  getEffectiveLogUid,
  getOrCreateVisitorId,
} from "./visitorIdentityService";

const LOGS_COLLECTION = "logs";
const PENDING_LOGS_STORAGE_KEY = "pending_logs";
const FIRST_VISIT_LOGGED_STORAGE_KEY = "first_visit_logged_v1";
const FIRST_VISIT_ANALYTICS_SENT_STORAGE_KEY = "first_visit_analytics_sent_v1";
const MAX_LOG_SANITIZE_DEPTH = 8;
const MAX_PENDING_ANALYTICS_EVENTS = 64;
const MAX_PENDING_ANALYTICS_EVENT_AGE_MS = 10 * 60 * 1000;
const PAGE_VIEW_DEDUP_WINDOW_MS = 1200;
const TRANSPORT_WARNING_DEBOUNCE_MS = 120_000;
let firstVisitLoggingPromise: Promise<void> | null = null;
let firstVisitBootstrapTriggered = false;
let firestoreLoggingDisabled = false;
let analyticsQueueFlushPromise: Promise<void> | null = null;
let lastPageViewSignature: string | null = null;
let lastPageViewAt = 0;
let lastTransportWarningAt = 0;
let currentOnboardingName: string | null = null;

function isPermissionDeniedError(error: unknown): boolean {
  if (!error) return false;
  const code = (error as { code?: unknown }).code;
  if (typeof code === "string" && code.toLowerCase().includes("permission-denied")) {
    return true;
  }
  const message = error instanceof Error ? error.message : String(error);
  return message.toLowerCase().includes("missing or insufficient permissions");
}

function sanitizeForFirestore(
  value: unknown,
  seen: WeakSet<object> = new WeakSet(),
  depth = 0
): unknown {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === "function" || typeof value === "symbol") return undefined;
  if (typeof value === "bigint") return value.toString();
  if (value instanceof Date) return value.toISOString();
  if (depth >= MAX_LOG_SANITIZE_DEPTH) return "[MaxDepth]";
  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeForFirestore(entry, seen, depth + 1))
      .filter((entry) => entry !== undefined);
  }
  if (typeof value === "object") {
    if (seen.has(value)) return "[Circular]";
    seen.add(value);
    const result: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, entry]) => {
      const sanitized = sanitizeForFirestore(entry, seen, depth + 1);
      if (sanitized !== undefined) {
        result[key] = sanitized;
      }
    });
    return result;
  }
  return value;
}

function sanitizeParams(
  params?: Record<string, unknown>
): Record<string, unknown> | undefined {
  if (!params) return undefined;
  const sanitized = sanitizeForFirestore(params) as Record<string, unknown>;
  if (!sanitized || Object.keys(sanitized).length === 0) {
    return undefined;
  }
  return sanitized;
}

function getClientContext(): Record<string, unknown> {
  if (typeof window === "undefined") {
    return {};
  }

  const context: Record<string, unknown> = {};
  try {
    const nav = window.navigator as Navigator & {
      userAgentData?: {
        platform?: string;
        mobile?: boolean;
        brands?: Array<{ brand: string }>;
      };
    };

    if (nav?.userAgent) context.userAgent = nav.userAgent;
    if (nav?.language) context.language = nav.language;
    const platform = nav?.userAgentData?.platform || nav?.platform;
    if (platform) context.platform = platform;
    context.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const userAgentData = nav?.userAgentData;
    if (typeof userAgentData?.mobile === "boolean") {
      context.deviceType = userAgentData.mobile ? "mobile" : "desktop";
    } else if (nav?.userAgent) {
      const ua = nav.userAgent;
      if (/iPad|Tablet/i.test(ua)) context.deviceType = "tablet";
      else if (/Mobi|Android/i.test(ua)) context.deviceType = "mobile";
      else context.deviceType = "desktop";
    }

    if (nav?.language) {
      const localeParts = nav.language.split("-");
      if (localeParts.length > 1) {
        context.countryCode = localeParts[1].toUpperCase();
      }
    }

  } catch (error) {
    console.warn("Failed to read client context:", error);
  }

  return context;
}

function getIsTester(): boolean | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const hostname = window.location?.hostname?.toLowerCase();
  if (!hostname) return undefined;

  const isProductionHost =
    hostname === "kinkyfantasy.ai" ||
    hostname === "www.kinkyfantasy.ai" ||
    hostname.endsWith(".kinkyfantasy.ai");

  return !isProductionHost;
}

function withClientContext(
  params?: Record<string, unknown>
): Record<string, unknown> | undefined {
  const clientContext = getClientContext();
  const isTester = getIsTester();
  if (Object.keys(clientContext).length === 0) {
    if (isTester === undefined) {
      return params;
    }
    return {
      ...(params ?? {}),
      isTester,
    };
  }

  const existingClient = params?.client;
  const mergedClient =
    existingClient &&
    typeof existingClient === "object" &&
    !Array.isArray(existingClient)
      ? { ...clientContext, ...existingClient }
      : clientContext;

  return {
    ...(params ?? {}),
    client: mergedClient,
    ...(isTester !== undefined ? { isTester } : {}),
  };
}

/**
 * Sets the onboarding quiz version ("v1"|"v2"|"v3"|"v4"|"v5") that gets injected into
 * every subsequent logEvent/logScreenMounted call, so the whole onboarding funnel
 * (not just onboarding_started) can be segmented by quiz version. Call with null
 * to clear it once the user leaves the onboarding flow.
 */
export function setOnboardingAnalyticsContext(onboardingName: string | null): void {
  currentOnboardingName = onboardingName;
}

function withOnboardingContext(
  params?: Record<string, unknown>
): Record<string, unknown> | undefined {
  if (!currentOnboardingName) {
    return params;
  }
  // Ambient value first, explicit params spread after: an explicitly-passed
  // onboardingName (e.g. on onboarding_started) must keep winning.
  return { onboardingName: currentOnboardingName, ...(params ?? {}) };
}

type LogValue = string | null;

function capturePostHogEvent(
  name: string,
  params?: Record<string, unknown>,
  value?: LogValue
): void {
  try {
    const posthogParams =
      value === null ? params : { ...(params ?? {}), value: value ?? undefined };
    posthog.capture(name, posthogParams);
  } catch (error) {
    console.warn("PostHog log failed:", name, error);
  }
}

function normalizeLogValue(value: unknown): LogValue {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry)).join(",");
  }
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

function getLandingPageForFirstVisit(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window.location.href;
}

function getRawUtmParamsForFirstVisit(): Record<string, string> {
  if (typeof window === "undefined") {
    return {};
  }

  const searchParams = new URLSearchParams(window.location.search);
  const utmKeys = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_id",
    "utm_source_platform",
    "utm_creative_format",
    "utm_marketing_tactic",
    "utm_content",
    "utm_term",
    "exocode",
  ] as const;

  const rawUtm: Record<string, string> = {};
  for (const key of utmKeys) {
    const value = searchParams.get(key);
    if (value) {
      rawUtm[key] = value;
    }
  }

  return rawUtm;
}

function getReferrerPageForFirstVisit(): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }

  const referrer = document.referrer?.trim();
  return referrer || undefined;
}

async function writeLogEntry(
  name: string,
  value: LogValue,
  params?: Record<string, unknown>,
  timestamp: Date = new Date()
): Promise<void> {
  if (firestoreLoggingDisabled) {
    return;
  }
  const uid = getEffectiveLogUid();

  const sanitizedValue = sanitizeForFirestore(value) as LogValue;
  const enrichedParams = sanitizeParams(withClientContext(params));

  const logEntry: LogEntry = {
    name,
    value: sanitizedValue ?? null,
    uid,
    timestamp,
    ...(enrichedParams ? { params: enrichedParams } : {}),
  };

  await addDoc(collection(db, LOGS_COLLECTION), logEntry);
}

type PendingLogEntry = {
  name: string;
  value: LogValue;
  params?: Record<string, unknown>;
  timestamp: string;
};

type PendingAnalyticsEntry =
  | {
      kind: "event";
      eventName: string;
      eventParams?: Record<string, unknown>;
      createdAt: number;
      firstVisitEvent?: boolean;
    }
  | {
      kind: "screen";
      slug: string;
      createdAt: number;
    }
  | {
      kind: "page_view";
      pagePath?: string;
      createdAt: number;
    };

const pendingAnalyticsEvents: PendingAnalyticsEntry[] = [];

function readPendingLogs(): PendingLogEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(PENDING_LOGS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn("Failed to read pending logs:", error);
    return [];
  }
}

function writePendingLogs(entries: PendingLogEntry[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PENDING_LOGS_STORAGE_KEY, JSON.stringify(entries));
  } catch (error) {
    console.warn("Failed to store pending logs:", error);
  }
}

function enqueuePendingLog(entry: PendingLogEntry): void {
  const entries = readPendingLogs();
  entries.push(entry);
  writePendingLogs(entries);
}

function isFirstVisitLogged(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.localStorage.getItem(FIRST_VISIT_LOGGED_STORAGE_KEY) === "1";
  } catch (error) {
    console.warn("Failed to read first_visit marker:", error);
    return false;
  }
}

function markFirstVisitLogged(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(FIRST_VISIT_LOGGED_STORAGE_KEY, "1");
  } catch (error) {
    console.warn("Failed to persist first_visit marker:", error);
  }
}

function isFirstVisitAnalyticsSent(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return (
      window.localStorage.getItem(FIRST_VISIT_ANALYTICS_SENT_STORAGE_KEY) === "1"
    );
  } catch (error) {
    console.warn("Failed to read first_visit analytics marker:", error);
    return false;
  }
}

function markFirstVisitAnalyticsSent(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(FIRST_VISIT_ANALYTICS_SENT_STORAGE_KEY, "1");
  } catch (error) {
    console.warn("Failed to persist first_visit analytics marker:", error);
  }
}

function isDuplicatePageView(path: string): boolean {
  const now = Date.now();
  if (
    lastPageViewSignature === path &&
    now - lastPageViewAt < PAGE_VIEW_DEDUP_WINDOW_MS
  ) {
    return true;
  }
  lastPageViewSignature = path;
  lastPageViewAt = now;
  return false;
}

function pruneExpiredPendingAnalytics(): void {
  const cutoff = Date.now() - MAX_PENDING_ANALYTICS_EVENT_AGE_MS;
  for (let i = pendingAnalyticsEvents.length - 1; i >= 0; i -= 1) {
    if (pendingAnalyticsEvents[i].createdAt < cutoff) {
      pendingAnalyticsEvents.splice(i, 1);
    }
  }
}

function enqueuePendingAnalytics(entry: PendingAnalyticsEntry): void {
  pruneExpiredPendingAnalytics();
  pendingAnalyticsEvents.push(entry);
  if (pendingAnalyticsEvents.length > MAX_PENDING_ANALYTICS_EVENTS) {
    pendingAnalyticsEvents.splice(
      0,
      pendingAnalyticsEvents.length - MAX_PENDING_ANALYTICS_EVENTS
    );
  }
}

function logDroppedPendingAnalytics(
  entry: PendingAnalyticsEntry,
  result: AnalyticsDispatchResult
): void {
  const eventName =
    entry.kind === "event"
      ? entry.eventName
      : entry.kind === "screen"
        ? `${entry.slug}_visited`
        : "page_view";
  console.info("[analytics] dropped queued event", {
    eventName,
    status: result.status,
    reason: result.reason ?? "unspecified",
  });
}

function logTransportUnhealthy(
  entry: PendingAnalyticsEntry,
  result: AnalyticsDispatchResult
): void {
  if (result.transportHealth !== "unhealthy") {
    return;
  }
  const now = Date.now();
  if (now - lastTransportWarningAt < TRANSPORT_WARNING_DEBOUNCE_MS) {
    return;
  }
  lastTransportWarningAt = now;

  const eventName =
    entry.kind === "event"
      ? entry.eventName
      : entry.kind === "screen"
        ? `${entry.slug}_visited`
        : "page_view";

  console.warn("[analytics] runtime initialized but no GA egress observed", {
    eventName,
    status: result.status,
    transportHealth: result.transportHealth,
    transportReason: result.transportReason ?? result.reason ?? "unspecified",
  });
}

async function dispatchPendingAnalyticsEntry(
  entry: PendingAnalyticsEntry
): Promise<AnalyticsDispatchResult> {
  if (entry.kind === "event") {
    return logAnalyticsEvent(entry.eventName, entry.eventParams);
  }
  if (entry.kind === "screen") {
    return logAnalyticsScreenMounted(entry.slug);
  }
  return logAnalyticsPageView(entry.pagePath);
}

async function flushPendingAnalyticsEvents(): Promise<void> {
  pruneExpiredPendingAnalytics();
  if (pendingAnalyticsEvents.length === 0) {
    return;
  }

  const readiness = getAnalyticsDispatchReadiness();
  if (readiness.status === "blocked_by_region_unavailable") {
    return;
  }

  while (pendingAnalyticsEvents.length > 0) {
    const entry = pendingAnalyticsEvents[0];
    const result = await dispatchPendingAnalyticsEntry(entry);
    if (result.sent) {
      if (entry.kind === "event" && entry.firstVisitEvent) {
        markFirstVisitAnalyticsSent();
      }
      pendingAnalyticsEvents.shift();
      continue;
    }

    if (getAnalyticsQueueDecision(result) === "queue") {
      // Region data is still unavailable; keep the event queued.
      return;
    }

    logDroppedPendingAnalytics(entry, result);
    pendingAnalyticsEvents.shift();
  }
}

function schedulePendingAnalyticsFlush(): void {
  if (analyticsQueueFlushPromise) {
    return;
  }
  analyticsQueueFlushPromise = flushPendingAnalyticsEvents().finally(() => {
    analyticsQueueFlushPromise = null;
    if (pendingAnalyticsEvents.length > 0) {
      const readiness = getAnalyticsDispatchReadiness();
      if (readiness.status !== "blocked_by_region_unavailable") {
        schedulePendingAnalyticsFlush();
      }
    }
  });
}

async function ensureFirstVisitBeforeEvent(eventName: string): Promise<void> {
  if (eventName === "first_visit" || isFirstVisitLogged()) {
    return;
  }
  if (firstVisitLoggingPromise) {
    await firstVisitLoggingPromise;
    return;
  }

  if (!firstVisitLoggingPromise) {
    firstVisitLoggingPromise = (async () => {
      getOrCreateVisitorId();
      await logEvent("first_visit", getUTMParametersForAnalytics());
    })().finally(() => {
      firstVisitLoggingPromise = null;
    });
  }

  await firstVisitLoggingPromise;
}

async function flushPendingLogs(): Promise<void> {
  if (firestoreLoggingDisabled) {
    return;
  }
  const entries = readPendingLogs();
  if (entries.length === 0) return;
  const remainingEntries = [...entries];

  for (const entry of entries) {
    try {
      await writeLogEntry(
        entry.name,
        entry.value,
        entry.params,
        new Date(entry.timestamp)
      );
      remainingEntries.shift();
      writePendingLogs(remainingEntries);
    } catch (error) {
      if (isPermissionDeniedError(error)) {
        firestoreLoggingDisabled = true;
        writePendingLogs([]);
        console.warn("Firestore log writes disabled due to permissions.");
        return;
      }
      writePendingLogs(remainingEntries);
      console.warn("Failed to flush pending log:", entry.name, error);
      return;
    }
  }
}

if (typeof window !== "undefined") {
  void flushPendingLogs();
  onAuthStateChanged(auth, (user) => {
    if (user) {
      void flushPendingLogs();
    }
  });
}

function trackAnalyticsResult(
  entry: PendingAnalyticsEntry,
  result: AnalyticsDispatchResult
): void {
  if (result.sent) {
    logTransportUnhealthy(entry, result);
    if (
      entry.kind === "event" &&
      shouldMarkFirstVisitAnalyticsDispatched(
        entry.eventName,
        result,
        isFirstVisitAnalyticsSent()
      )
    ) {
      markFirstVisitAnalyticsSent();
    }
    return;
  }

  if (getAnalyticsQueueDecision(result) === "queue") {
    enqueuePendingAnalytics(entry);
    schedulePendingAnalyticsFlush();
    return;
  }

  logDroppedPendingAnalytics(entry, result);
}

export async function logEvent(
  name: string,
  params?: Record<string, unknown>,
  value: LogValue = null
): Promise<void> {
  if (name === "first_visit" && isFirstVisitLogged()) {
    return;
  }
  if (name !== "first_visit") {
    await ensureFirstVisitBeforeEvent(name);
  }

  const effectiveParams =
    name === "first_visit"
      ? {
          ...getUTMParametersForAnalytics(),
          ...getRawUtmParamsForFirstVisit(),
          landing_page_full_url: getLandingPageForFirstVisit(),
          referrer_page_url: getReferrerPageForFirstVisit(),
          ...(params ?? {}),
        }
      : params;
  const sanitizedParams = sanitizeParams(withOnboardingContext(effectiveParams));
  const normalizedValue = normalizeLogValue(value);
  const timestamp = new Date();

  const analyticsResult = await logAnalyticsEvent(name, sanitizedParams);
  trackAnalyticsResult(
    {
      kind: "event",
      eventName: name,
      eventParams: sanitizedParams,
      createdAt: timestamp.getTime(),
      firstVisitEvent: name === "first_visit" && !isFirstVisitAnalyticsSent(),
    },
    analyticsResult
  );
  capturePostHogEvent(name, sanitizedParams, normalizedValue);

  try {
    await writeLogEntry(name, normalizedValue, sanitizedParams, timestamp);
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      firestoreLoggingDisabled = true;
      writePendingLogs([]);
      console.warn("Firestore log writes disabled due to permissions.");
      return;
    }
    enqueuePendingLog({
      name,
      value: normalizedValue,
      params: sanitizedParams,
      timestamp: timestamp.toISOString(),
    });
    console.warn("Failed to write log entry:", name, error);
  }

  if (name === "first_visit") {
    markFirstVisitLogged();
  }
}

export async function logABTestExposure(
  testValue: string,
  params?: Record<string, unknown>
): Promise<void> {
  if (!testValue) return;
  await logEvent("Changed: Active split tests", params, testValue);
}

export async function logScreenMounted(
  slug?: string,
  params?: Record<string, unknown>
): Promise<void> {
  if (!slug) return;
  const sanitizedParams = sanitizeParams(withOnboardingContext(params));
  const eventName = slug.replace(/-/g, "_").substring(0, 30) + "_visited";
  const timestamp = new Date();
  await ensureFirstVisitBeforeEvent(eventName);

  const analyticsResult = await logAnalyticsScreenMounted(slug);
  trackAnalyticsResult(
    {
      kind: "screen",
      slug,
      createdAt: timestamp.getTime(),
    },
    analyticsResult
  );
  capturePostHogEvent(eventName, sanitizedParams);

  try {
    await writeLogEntry(eventName, null, sanitizedParams, timestamp);
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      firestoreLoggingDisabled = true;
      writePendingLogs([]);
      console.warn("Firestore log writes disabled due to permissions.");
      return;
    }
    enqueuePendingLog({
      name: eventName,
      value: null,
      params: sanitizedParams,
      timestamp: timestamp.toISOString(),
    });
    console.warn("Failed to write screen log:", eventName, error);
  }
}

export async function logPageView(pagePath?: string): Promise<void> {
  if (typeof window === "undefined") {
    return;
  }

  const path = pagePath || window.location.pathname;
  if (isDuplicatePageView(path)) {
    return;
  }

  const params = {
    page_path: path,
    page_location: window.location.href,
  };
  const sanitizedParams = sanitizeParams(params);
  const timestamp = new Date();
  await ensureFirstVisitBeforeEvent("page_view");

  const analyticsResult = await logAnalyticsPageView(pagePath);
  trackAnalyticsResult(
    {
      kind: "page_view",
      pagePath,
      createdAt: timestamp.getTime(),
    },
    analyticsResult
  );
  capturePostHogEvent("page_view", sanitizedParams);

  try {
    await writeLogEntry("page_view", null, sanitizedParams, timestamp);
  } catch (error) {
    if (isPermissionDeniedError(error)) {
      firestoreLoggingDisabled = true;
      writePendingLogs([]);
      console.warn("Firestore log writes disabled due to permissions.");
      return;
    }
    enqueuePendingLog({
      name: "page_view",
      value: null,
      params: sanitizedParams,
      timestamp: timestamp.toISOString(),
    });
    console.warn("Failed to write page view log:", pagePath, error);
  }
}

export function notifyAnalyticsContextUpdated(): void {
  schedulePendingAnalyticsFlush();
}

export function initializeFirstVisitTracking(): void {
  if (typeof window === "undefined") {
    return;
  }
  if (firstVisitBootstrapTriggered || isFirstVisitLogged()) {
    return;
  }

  firstVisitBootstrapTriggered = true;
  void logEvent("first_visit").catch((error) => {
    console.warn("Failed to initialize first_visit tracking:", error);
  });
}
